import gql from 'graphql-tag';

// QUERIES

const getBookletsQuery = gql`
  {
    booklets: booklets {
      _id
      title
      subtitle
      buyableFrom
      buyableTo
      validFrom
      validTo
      price
      zoneId
      logoURL
      colorLeft
      colorRight
      colorFont
      colorSalesMaterial
      couponIds
      imageOfBookletInAppURL
      imageOfSingleBookletInAppURL
      shopURL
      purchasableThroughAssociations
      mainAssociationId
      adminEmails
      categorySettings {
        categoryId
        color
        order
      }
    }
  }
`;

const getBookletsInZoneQuery = gql`
  query ($zoneId: ObjectId!) {
    booklets: bookletsInZone(zoneId: $zoneId) {
      _id
      title
      subtitle
      adminEmails
      buyableFrom
      buyableTo
      validFrom
      validTo
      price
      totalCommission
      supportCommissionToAssociation
      supportCommissionToMainAssociation
      soldBooklets
      usedOrders
      logoURL
      colorLeft
      colorRight
      colorFont
      colorSalesMaterial
      couponIds
      imageOfBookletInAppURL
      imageOfSingleBookletInAppURL
      shopURL
      purchasableThroughAssociations
      mainAssociationId
      categorySettings {
        categoryId
        color
        order
      }
      textInsidePrintedBooklet
      shouldSendStatsMail
    }
  }
`;

const getAdminableBookletsQuery = gql`
  query ($zoneId: ObjectId!) {
    booklets: myAdminableBooklets(zoneId: $zoneId) {
      _id
      title
      couponIds
      buyableFrom
      buyableTo
      validFrom
      validTo
      price
      zoneId
      mainAssociationId
      adminEmails
    }
  }
`;

const getBuyableBookletsQuery = gql`
  {
    booklets: buyableBooklets{
      _id
      title
      validFrom
      validTo
      price
      logoURL
      colorLeft
      colorRight
      colorFont
      zoneId
      purchasableThroughAssociations
      imageOfBookletInAppURL
      imageOfSingleBookletInAppURL
      mainAssociationId
      supportCommissionToMainAssociation
    }
  }
`;

const getDashboardAssociationBookletsQuery = gql`
  query getDashboardAssociationBooklets($associationId: ObjectId!, $teamId: ObjectId) {
    booklets: dashboardAssociationBooklets(associationId: $associationId, teamId: $teamId) {
      _id
      title
      subtitle
      validFrom
      validTo
      price
      logoURL
      colorLeft
      colorRight
      colorFont
      zoneId
      purchasableThroughAssociations
      imageOfBookletInAppURL
      imageOfSingleBookletInAppURL
      buyableFrom
      buyableTo
      mainAssociationId
      supportCommissionToMainAssociation
      supportCommissionToAssociation
      totalCommission
    }
  }
`;

const getBookletQuery = gql`
  query($_id: ObjectId!) {
    booklet: booklet(_id: $_id) {
      _id
      title
      subtitle
      validFrom
      validTo
      colorLeft
      colorRight
      colorFont
      colorSalesMaterial
      logoURL
      userOwnsBooklet
      price
      imageOfBookletInAppURL
      imageOfSingleBookletInAppURL
      shopURL
      categorySettings {
        categoryId
        color
        order
      }
      textInsidePrintedBooklet
      calculatedCompanies {
        _id
        name
        logoURL
        websiteURL
        typeId
        companyCategoryId
        phone
        visitAddress {
          streetAddress
          city
        }
        calculatedCoupons {
          _id
          discount 
          description
          smallTitle
          productLink
          availableByUser
          terms
          imageURL
          companyId
        }
      }
    }
  }
`;

const getBookletCompaniesCountQuery = gql`
  query($_id: ObjectId!) {
    booklet: booklet(_id: $_id) {
      calculatedCompanies {
        _id
        calculatedCoupons {
          _id
        }
      }
    }
  }
`;

const getBookletNameQuery = gql`
  query BookletName($_id: ObjectId!) {
    booklet: booklet(_id: $_id) {
      title
    }
  }
`;

const categoriesInBookletQuery = gql`query CategoriesInBooklet($bookletId: ObjectId!) {
  categoriesInBooklet(bookletId: $bookletId) {
    name,
    _id,
    iconPath
  }
}`;

const companyFromBookletQuery = gql`query CompanyFromBooklet($bookletId: ObjectId!, $companyId: ObjectId!) {
  companyFromBooklet(bookletId: $bookletId, companyId: $companyId
  ) {
    _id
    name
    logoURL
    websiteURL
    typeId
    companyCategoryId
    phone
    visitAddress {
      streetAddress
      city
    }
    calculatedCoupons {
      _id
      discount 
      description
      smallTitle
      productLink
      availableByUser
      terms
      imageURL
      position
      assessment {
        rating
        motivation
        expectedEffect
        profitability
        targetGroupAdaptation
        shouldShowAssessment
      }
      code
      codeType
    }
  }
}`;

const getAdminResellerStatsQuery = gql`
  query ($bookletId: ObjectId!, $associationId: ObjectId!, $fromDate: DateTime, $toDate: DateTime) {
    stats: bookletResellerStats(bookletId: $bookletId, associationId: $associationId, fromDate: $fromDate, toDate: $toDate) {
      associations {
        displayName
        sold
        supportSold
        earnings
        afEarnings
        supportEarnings
        association {
          _id
          name
          phone
          shopShareDomain
          salesStartDate
          salesEndDate
          paidOutByBooklet {
            bookletId
            amount
          }
        }
      }
      teams {
        displayName
        sold
        earnings
        afEarnings
        supportEarnings
        team {
          _id
          associationId
          name
          salesStartDate
          salesEndDate
          paidOutByBooklet {
            bookletId
            amount
          }
        }
      }
      teamMembers {
        displayName
        sold
        earnings
        afEarnings
        supportEarnings
        teamMember {
          _id
          teamId
        }
      }
      totals {
        totalSold
        totalEarnings
      }
      commissionSettings {
        totalCommission
        supportCommissionToMainAssociation
        supportCommissionToAssociation
      }
    }
  }
`;

const getResellerStatsQuery = gql`
  query ($bookletId: ObjectId!, $associationId: ObjectId!, $fromDate: DateTime, $toDate: DateTime) {
    stats: bookletResellerStats(bookletId: $bookletId, associationId: $associationId, fromDate: $fromDate, toDate: $toDate) {
      associations {
        displayName
        sold
        supportSold
        earnings
        afEarnings
        supportEarnings
        isSupportable
        association {
          _id
        }
      }
      teams {
        displayName
        sold
        earnings
        afEarnings
        supportEarnings
        team {
          _id
          associationId
        }
      }
      teamMembers {
        displayName
        sold
        earnings
        afEarnings
        supportEarnings
        teamMember {
          _id
          teamId
        }
      }
      totals {
        totalSold
        totalEarnings
      }
      commissionSettings {
        totalCommission
        supportCommissionToMainAssociation
        supportCommissionToAssociation
      }
    }
  }
`;

const getBookletByReviewTokenQuery = gql`query($token: String!) {
  bookletByReviewToken(token: $token
  ) {
    validTo
    colorLeft
    colorRight
    logoURL
    title
    subtitle
    colorFont
    calculatedCompanies {
      _id
      name
      logoURL
      websiteURL
      typeId
      companyCategoryId
      phone
      visitAddress {
        streetAddress
        city
      }
      calculatedCoupons {
        _id
        discount 
        description
        smallTitle
        productLink
        availableByUser
        terms
        imageURL
        companyId
        code
        codeType
      }
    }
  }
}`;



// MUTATIONS

const addBookletMutation = gql`
  mutation(
    $title: String!
    $subtitle: String
    $adminEmails: [String!]
    $buyableFrom: DateTime!
    $buyableTo: DateTime!
    $validFrom: DateTime!
    $validTo: DateTime!
    $price: Float
    $totalCommission: Float
    $supportCommissionToMainAssociation: Float
    $supportCommissionToAssociation: Float
    $logoURL: String
    $colorLeft: String
    $colorRight: String
    $colorFont: String
    $colorSalesMaterial: String
    $imageOfBookletInAppURL: String
    $imageOfSingleBookletInAppURL: String
    $purchasableThroughAssociations: [String!]
    $zoneId: ObjectId!
    $mainAssociationId: ObjectId
    $textInsidePrintedBooklet: String
    $bookletSeriesId: ObjectId
    $shouldSendStatsMail: Boolean!
    ) {
    createBooklet(
      title: $title
      subtitle: $subtitle
      adminEmails: $adminEmails,
      buyableFrom: $buyableFrom,
      buyableTo: $buyableTo,
      validFrom: $validFrom,
      validTo: $validTo,
      price: $price,
      totalCommission: $totalCommission,
      supportCommissionToMainAssociation: $supportCommissionToMainAssociation,
      supportCommissionToAssociation: $supportCommissionToAssociation,
      logoURL: $logoURL,
      colorLeft: $colorLeft,
      colorRight: $colorRight,
      colorFont: $colorFont,
      colorSalesMaterial: $colorSalesMaterial,
      imageOfBookletInAppURL: $imageOfBookletInAppURL,
      imageOfSingleBookletInAppURL: $imageOfSingleBookletInAppURL,
      purchasableThroughAssociations: $purchasableThroughAssociations,
      zoneId: $zoneId,
      mainAssociationId: $mainAssociationId,
      textInsidePrintedBooklet: $textInsidePrintedBooklet,
      bookletSeriesId: $bookletSeriesId,
      shouldSendStatsMail: $shouldSendStatsMail
      ) {
        title
    }
  }
`;

const updateBookletMutation = gql`
  mutation(
    $_id: ObjectId!
    $title: String!
    $subtitle: String
    $adminEmails: [String!]
    $buyableFrom: DateTime!
    $buyableTo: DateTime!
    $validFrom: DateTime!
    $validTo: DateTime!
    $price: Float
    $totalCommission: Float
    $supportCommissionToMainAssociation: Float
    $supportCommissionToAssociation: Float
    $logoURL: String
    $colorLeft: String
    $colorRight: String
    $colorFont: String
    $colorSalesMaterial: String
    $couponIds: [String!]
    $imageOfBookletInAppURL: String
    $imageOfSingleBookletInAppURL: String
    $purchasableThroughAssociations: [String!]
    $mainAssociationId: ObjectId
    $categorySettings: [BookletCategorySettingsInput]
    $textInsidePrintedBooklet: String
    $bookletSeriesId: ObjectId
    $shouldSendStatsMail: Boolean!
    ) {
    updateBooklet(
      _id: $_id,
      title: $title
      subtitle: $subtitle
      adminEmails: $adminEmails,
      buyableFrom: $buyableFrom,
      buyableTo: $buyableTo,
      validFrom: $validFrom,
      validTo: $validTo,
      price: $price,
      totalCommission: $totalCommission,
      supportCommissionToMainAssociation: $supportCommissionToMainAssociation,
      supportCommissionToAssociation: $supportCommissionToAssociation,
      logoURL: $logoURL,
      colorLeft: $colorLeft,
      colorRight: $colorRight,
      colorFont: $colorFont,
      colorSalesMaterial: $colorSalesMaterial,
      couponIds: $couponIds,
      imageOfBookletInAppURL: $imageOfBookletInAppURL,
      imageOfSingleBookletInAppURL: $imageOfSingleBookletInAppURL,
      purchasableThroughAssociations: $purchasableThroughAssociations,
      mainAssociationId: $mainAssociationId,
      categorySettings: $categorySettings,
      textInsidePrintedBooklet: $textInsidePrintedBooklet,
      bookletSeriesId: $bookletSeriesId,
      shouldSendStatsMail: $shouldSendStatsMail
      ) {
        _id
        title
    }
  }
`;

const updateCouponsInBookletMutation = gql`
  mutation(
    $_id: ObjectId!
    $title: String!
    $subtitle: String
    $couponIds: [String!]
    ) {
    updateBooklet(
      _id: $_id,
      title: $title
      subtitle: $subtitle
      couponIds: $couponIds
      ) {
        _id
        title
        couponIds
    }
  }
`;

const generateCompletedOrderBatchPDF = gql`
  mutation(
    $batchLabel: String!,
    $bookletId: ObjectId!,
    $mode: String!
    ) {
    generateCompletedOrderBatchPDF(
      batchLabel: $batchLabel,
      bookletId: $bookletId,
      mode: $mode
    )
  }
`;

const generateCompletedOrderPDF = gql`
  mutation(
    $bookletId: ObjectId!,
    $orderId: ObjectId!,
    $encryptedOrderCode: String!
  ) {
    generateCompletedOrderPDF(
      bookletId: $bookletId,
      orderId: $orderId,
      encryptedOrderCode: $encryptedOrderCode
    )
  }
`;

const generatePrintedCompletedOrderBatchPDF = gql`
  mutation(
    $batchLabel: String!,
    $bookletId: ObjectId!,
    $mode: String!
    ) {
    generatePrintedCompletedOrderBatchPDF(
      batchLabel: $batchLabel,
      bookletId: $bookletId,
      mode: $mode
    )
  }
`;

const generateSalesMaterial = gql`
  mutation($bookletId: ObjectId!, $associationId: ObjectId, $teamId: ObjectId, $type: String!, $posterType: PosterType) {
    generateSalesMaterial(bookletId: $bookletId, associationId: $associationId, teamId: $teamId, type: $type, posterType: $posterType)
  }
`;

const sendOrderBatchPdfLink = gql`
  mutation(
    $email: Email!,
    $bookletId: ObjectId!,
    $batchLabel: String!,
    $portalUrlForEmails: String!
  ) {
    sendOrderBatchPdfLink(
      email: $email,
      bookletId: $bookletId,
      batchLabel: $batchLabel,
      portalUrlForEmails: $portalUrlForEmails
    )
  }
`;

const sendOrderPdfLink = gql`
  mutation(
    $emails: [Email!]!,
    $bookletId: ObjectId!,
    $orderCode: String!,
    $orderId: ObjectId!,
    $portalUrlForEmails: String!
    $title: String!
    $message: String!
    $companyId: ObjectId!
    $zoneId: ObjectId!
    $replyTo: String
  ) {
    sendOrderPdfLink(
      emails: $emails,
      bookletId: $bookletId,
      orderCode: $orderCode,
      orderId: $orderId,
      portalUrlForEmails: $portalUrlForEmails,
      title: $title,
      message: $message,
      companyId: $companyId,
      zoneId: $zoneId,
      replyTo: $replyTo
    )
  }
`;

const cloneBookletMutation = gql`
  mutation CloneBooklet(
    $_id: ObjectId!
    $buyableFrom: DateTime!
    $buyableTo: DateTime!
    $validFrom: DateTime!
    $validTo: DateTime!
  ) {
    cloneBooklet(
      _id: $_id
      buyableFrom: $buyableFrom
      buyableTo: $buyableTo
      validFrom: $validFrom
      validTo: $validTo
    ) {
      _id
      title
    }
  }
`;

export {
  getBookletsQuery,
  getBookletsInZoneQuery,
  getBuyableBookletsQuery,
  getDashboardAssociationBookletsQuery,
  getBookletQuery,
  getBookletCompaniesCountQuery,
  getBookletNameQuery,
  getAdminableBookletsQuery,
  getBookletByReviewTokenQuery,
  getAdminResellerStatsQuery as getBookletResellerStatsQuery,
  getResellerStatsQuery as getBookletResellerStatsSimpleQuery, categoriesInBookletQuery,
  companyFromBookletQuery,
  addBookletMutation,
  updateBookletMutation,
  updateCouponsInBookletMutation,
  generateCompletedOrderBatchPDF,
  generateCompletedOrderPDF,
  generatePrintedCompletedOrderBatchPDF,
  generateSalesMaterial,
  sendOrderBatchPdfLink,
  sendOrderPdfLink,
  cloneBookletMutation
};
